/**
 * Клиент аутентификации поверх внешнего OAuth2-сервиса (через bcf).
 *
 * Логин/логаут — браузерные редиректы на bcf-эндпоинты (`/_/api/oauth2/v1/*`):
 * bcf сам обменивает code на токены, кладёт сессию в Redis и ставит cookie.
 * Здесь — только URL-хелперы и запрос colver-профиля текущего пользователя
 * (`GET /users/self`): авторизуется session-cookie (или `X-Tests-User-Sid` в dev).
 */
import axios from 'axios';
import type {CurrentUser, TourKey} from '../types';
import {readStoredSid} from '../lib/storage';

/** База bcf-эндпоинтов (oauth2 login/logout). На стенде — префикс `/_`. */
const AUTH_BASE = import.meta.env.VITE_AUTH_BASE ?? '/_';

/** URL начала OAuth2-входа (browser redirect). */
export const oauth2LoginUrl = (redirectPage?: string): string => {
  const base = `${AUTH_BASE}/api/oauth2/v1/login`;
  if (!redirectPage) return base;
  return `${base}?redirect_page=${encodeURIComponent(redirectPage)}`;
};

/** URL выхода (browser redirect; bcf гасит сессию и чистит cookie). */
export const oauth2LogoutUrl = (): string =>
  `${AUTH_BASE}/api/oauth2/v1/logout`;

const http = axios.create({
  baseURL: import.meta.env.VITE_MEEREC_BASE ?? '/api/user/meerec/v1',
});

// Dev-режим: tests-wrapper авторизует по X-Tests-User-Sid (сохранённый sid).
// В prod заголовок игнорируется — авторизует session-cookie OAuth2.
const fallbackDevSid = import.meta.env.VITE_DEV_USER_SID;
http.interceptors.request.use((config) => {
  const sid = readStoredSid() ?? fallbackDevSid;
  if (sid) config.headers.set('X-Tests-User-Sid', sid);
  return config;
});

/** GET /users/self — colver-профиль текущего пользователя. 401 = нет сессии/учётки. */
const self = async (): Promise<CurrentUser> => {
  const {data} = await http.get<CurrentUser>('/users/self');
  return data;
};

/** PUT /users/self/seen-tours/{tourKey} — тур показан (идемпотентно). */
const markTourSeen = async (tourKey: TourKey): Promise<void> => {
  await http.put(`/users/self/seen-tours/${tourKey}`);
};

export const colverAuthClient = {
  self,
  markTourSeen,
  oauth2LoginUrl,
  oauth2LogoutUrl,
};
